import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, Table, Badge, Spinner } from 'react-bootstrap';
import { FaSearch, FaFilter, FaUserPlus, FaSyncAlt, FaEnvelope, FaCheckCircle } from 'react-icons/fa';

const InvitesModal = ({ show, onHide, evenementId }) => {
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatut, setSelectedStatut] = useState('all');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Options de filtre par statut
    const statutOptions = [
        { value: 'all', label: 'Tous', color: 'secondary' },
        { value: '1', label: 'Invitation envoyée', color: 'warning' },
        { value: '2', label: 'Sera présent', color: 'success' }
    ];

    // Récupérer les invités
    const fetchData = async () => {
        if (!evenementId) return;

        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(
                `${process.env.REACT_APP_HOTE}api/liste-invites-evenement/${evenementId}/`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                console.log("📦 Données reçues:", data);

                // ✅ Extraction du tableau d'invités
                let invitesList = [];
                if (Array.isArray(data)) {
                    invitesList = data;
                } else if (data.data && Array.isArray(data.data)) {
                    invitesList = data.data;
                } else if (data.results && Array.isArray(data.results)) {
                    invitesList = data.results;
                } else if (data.invites && Array.isArray(data.invites)) {
                    invitesList = data.invites;
                } else {
                    invitesList = [];
                }

                setInvites(invitesList);
                setTotalItems(invitesList.length);
            } else {
                const errorData = await response.json();
                setError(errorData.message || "Erreur lors du chargement des invités");
                setInvites([]);
                setTotalItems(0);
            }
        } catch (error) {
            console.error('Erreur réseau:', error);
            setError("Impossible de charger la liste des invités");
            setInvites([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    // ==================== USE EFFECTS ====================
    useEffect(() => {
        if (show && evenementId) {
            setCurrentPage(1);
            fetchData();
        }
    }, [show, evenementId]);

    // ==================== FONCTIONS DE FILTRAGE ====================
    // Dans la fonction getFilteredData (ou l'endroit où tu filtres)

    const getFilteredData = () => {
        let filtered = [...invites];

        // Filtre par statut - ✅ CORRECTION
        if (selectedStatut !== 'all') {
            filtered = filtered.filter(invite => {
                // Convertir en string pour comparaison
                const statutInvite = String(invite.statut_invitation);
                const statutFiltre = String(selectedStatut);
                return statutInvite === statutFiltre;
            });
        }

        // Filtre par recherche (inchangé)
        if (searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(invite =>
                (invite.pseudo && String(invite.pseudo).toLowerCase().includes(term)) ||
                (invite.telephone && String(invite.telephone).toLowerCase().includes(term)) ||
                (invite.ref_compte && String(invite.ref_compte).toLowerCase().includes(term)) ||
                (invite.nom && String(invite.nom).toLowerCase().includes(term)) ||
                (invite.prenoms && String(invite.prenoms).toLowerCase().includes(term)) ||
                (invite.email && String(invite.email).toLowerCase().includes(term))
            );
        }

        return filtered;
    };

    const filteredData = getFilteredData();
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const indexOfFirstItem = filteredData.length === 0 ? 0 : startIndex + 1;
    const indexOfLastItem = Math.min(endIndex, filteredData.length);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setSelectedStatut('all');
        setCurrentPage(1);
    };

    // ==================== FONCTIONS UTILITAIRES ====================
    const getStatutBadge = (statut) => {
        switch (statut) {
            case '1':
                return { variant: 'warning', text: 'Invitation envoyée', icon: <FaEnvelope className="me-1" size={12} /> };
            case '2':
                return { variant: 'success', text: 'Sera présent', icon: <FaCheckCircle className="me-1" size={12} /> };
            default:
                return { variant: 'secondary', text: 'Inconnu', icon: null };
        }
    };

    // ==================== RENDU ====================
    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title>
                    <FaUserPlus className="me-2 text-primary" />
                    Invités de l'événement
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Barre de recherche et filtres */}
                <div className="mb-4">
                    <div className="row g-2">
                        <div className="col-md-6">
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <FaSearch className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Rechercher par pseudo, téléphone, référence..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                                        ×
                                    </Button>
                                )}
                            </InputGroup>
                        </div>

                        <div className="col-md-4">
                            <InputGroup>
                                <InputGroup.Text className="bg-white">
                                    <FaFilter className="text-muted" />
                                </InputGroup.Text>
                                <Form.Select
                                    value={selectedStatut}
                                    onChange={(e) => setSelectedStatut(e.target.value)}
                                >
                                    {statutOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </InputGroup>
                        </div>

                        <div className="col-md-2">
                            <Button
                                variant="outline-primary"
                                onClick={fetchData}
                                disabled={loading}
                                className="w-100"
                            >
                                <FaSyncAlt className={loading ? "fa-spin" : ""} />
                                {' '}{loading ? 'Chargement...' : 'Rafraîchir'}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Sélecteur nombre d'éléments par page */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="form-select form-select-sm"
                            style={{ width: 'auto', display: 'inline-block' }}
                        >
                            <option value={5}>5 par page</option>
                            <option value={10}>10 par page</option>
                            <option value={15}>15 par page</option>
                            {/* <option value={50}>50 par page</option> */}
                        </select>
                    </div>

                    {(searchTerm || selectedStatut !== 'all') && (
                        <Button variant="link" size="sm" onClick={resetFilters} className="text-decoration-none">
                            Réinitialiser les filtres
                        </Button>
                    )}
                </div>

                {/* Résumé des résultats */}
                {!loading && filteredData.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <small className="text-muted">
                            Affichage de {indexOfFirstItem} à {indexOfLastItem} sur {filteredData.length} invité(s)
                            {selectedStatut !== 'all' && ` • Filtré par ${statutOptions.find(s => s.value === selectedStatut)?.label}`}
                            {searchTerm && ` • Recherche: "${searchTerm}"`}
                        </small>
                    </div>
                )}

                {/* Tableau des invités */}
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Chargement des invités...</p>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger text-center py-4">
                        <p className="mb-2">{error}</p>
                        <Button variant="outline-danger" size="sm" onClick={fetchData}>
                            Réessayer
                        </Button>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="text-center py-5">
                        <FaUserPlus size={50} className="text-muted mb-3" />
                        <p className="text-muted">
                            {invites.length === 0
                                ? "Aucun invité pour cet événement"
                                : "Aucun résultat ne correspond à vos critères"}
                        </p>
                        {(searchTerm || selectedStatut !== 'all') && (
                            <Button variant="link" onClick={resetFilters}>
                                Voir tous les invités
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <Table hover striped className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Réf. compte</th>
                                        <th>Pseudo / Nom</th>
                                        <th>Téléphone</th>
                                        <th>Statut invitation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedData.map((invite, index) => {
                                        const statutBadge = getStatutBadge(invite.statut_invitation);
                                        return (
                                            <tr key={invite.id || index}>
                                                <td>
                                                    <code className="small">{invite.ref_compte || '—'}</code>
                                                </td>
                                                <td>
                                                    <strong>{invite.pseudo || `${invite.prenoms || ''} ${invite.nom || ''}`}</strong>
                                                    <br /><small className="text-muted">{invite.email}</small>
                                                </td>
                                                <td>{invite.telephone || '—'}</td>
                                                <td>
                                                    <Badge bg={statutBadge.variant}>
                                                        {statutBadge.icon}
                                                        {statutBadge.text}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted small">
                                    Page {currentPage} sur {totalPages}
                                </div>
                                <div className="d-flex align-items-center">
                                    <button
                                        onClick={() => goToPage(currentPage - 1)}
                                        className="btn btn-sm btn-outline-secondary mx-1"
                                        disabled={currentPage === 1}
                                    >
                                        &laquo;
                                    </button>

                                    {Array.from({ length: Math.min(totalPages, 5) }, (_, index) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = index + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = index + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + index;
                                        } else {
                                            pageNum = currentPage - 2 + index;
                                        }
                                        if (pageNum < 1 || pageNum > totalPages) return null;
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => goToPage(pageNum)}
                                                className={`btn btn-sm mx-1 ${currentPage === pageNum ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    <button
                                        onClick={() => goToPage(currentPage + 1)}
                                        className="btn btn-sm btn-outline-secondary mx-1"
                                        disabled={currentPage === totalPages}
                                    >
                                        &raquo;
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onHide}>Fermer</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default InvitesModal;